import { useLinkProps, useNavigate } from "@tanstack/react-router"
import * as React from "react"

type ButtonSize = "xs" | "sm" | "md" | "lg"
type ButtonColor = "white" | "red"

export type ButtonProps = Omit<React.ComponentPropsWithoutRef<"button">, "type" | "onClick"> & {
  type?: React.ComponentPropsWithoutRef<"button">["type"] | "link"
  color?: ButtonColor
  size?: ButtonSize
  onClick?: (_event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => boolean | void
  loading?: boolean
  loadingText?: string
  native?: boolean
  href?: string
  to?: string
  params?: Record<string, string>
  search?: Record<string, unknown> | ((_previous: Record<string, unknown>) => Record<string, unknown>)
  iconLeft?: string
  iconRight?: string
  iconClassName?: string
  overrideClassName?: string
  ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement>
}

const Button = (props: ButtonProps) => {
  if (props.type === "link" && props.to) {
    return <TypedButtonLink {...props} />
  }

  return <LegacyButton {...props} />
}

const LegacyButton = ({
  disabled,
  color,
  size = "sm",
  onClick,
  children = "Button Label",
  type = "button",
  loading,
  loadingText = "Saving...",
  native: _native,
  href,
  className,
  iconLeft,
  iconRight,
  iconClassName,
  overrideClassName,
  ref,
  ...rest
}: ButtonProps) => {
  const extraClasses: (string | undefined)[] = [className]

  const sizeMap: Record<ButtonSize, string> = {
    xs: "c-button--xs",
    sm: "c-button--sm",
    md: "c-button--md",
    lg: "c-button--lg",
  }

  const colorMap: Record<ButtonColor, string> = {
    white: "c-button--white",
    red: "c-button--red",
  }

  const disabledColorMap: Record<ButtonColor, string> = {
    white: "c-button--white-disabled",
    red: "c-button--red-disabled",
  }

  if (disabled || loading) extraClasses.push("c-button--disabled")
  if ((disabled || loading) && color && disabledColorMap[color])
    extraClasses.push(disabledColorMap[color])
  if (color && colorMap[color]) extraClasses.push(colorMap[color])
  if (size && sizeMap[size]) extraClasses.push(sizeMap[size])

  const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // If link is disabled, stop anything from happening
    if (disabled === true || loading === true) {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    if (onClick) {
      if (onClick(event) === false) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
    }

    // Legacy href links now use native browser navigation.
  }

  if (type === "link") {
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        onClick={handleLinkClick}
        className={
          overrideClassName ||
          `ignore-old-css cursor-pointer c-button ${extraClasses.filter((c) => c).join(" ")}`
        }
        href={href}
      >
        <span className="flex gap-x-2 items-center">
          <ButtonIcon icon={iconLeft} buttonSize={size} className={iconClassName} />
          {loading && loadingText}
          {!loading && children}
          <ButtonIcon icon={iconRight} buttonSize={size} className={iconClassName} />
        </span>
      </a>
    )
  } else {
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        onClick={onClick}
        className={
          overrideClassName || `ignore-old-css c-button ${extraClasses.filter((c) => c).join(" ")}`
        }
        type={type}
        disabled={disabled || loading}
        {...rest}
      >
        <span className="flex gap-x-2 items-center">
          <ButtonIcon icon={iconLeft} buttonSize={size} className={iconClassName} />
          {loading && loadingText}
          {!loading && children}
          <ButtonIcon icon={iconRight} buttonSize={size} className={iconClassName} />
        </span>
      </button>
    )
  }
}

const TypedButtonLink = ({ onClick, to, params, search, native, ...props }: ButtonProps) => {
  const typedLink = useLinkProps({ to: to as string, params, search })
  const navigate = useNavigate()
  const handleClick = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (onClick && onClick(event) === false) {
      return false
    }

    // Leave `native` links to the browser, so they do a full page load
    if (native) {
      return
    }

    if (!event.metaKey && !event.ctrlKey && !event.shiftKey && event.button !== 1) {
      event.preventDefault()
      event.stopPropagation()
      navigate({ to: to as string, params, search })
    }
  }

  return <LegacyButton {...props} href={typedLink.href} native onClick={handleClick} type="link" />
}

interface ButtonIconProps {
  icon?: string
  buttonSize: ButtonSize
  className?: string
}

const ButtonIcon = ({ icon, buttonSize, className }: ButtonIconProps) => {
  if (!icon) return null
  const iconSizes: Record<ButtonSize, string> = {
    xs: "text-xs",
    sm: "text-xs",
    md: "text-sm",
    lg: "text-md",
  }
  return <i className={`${icon} ${iconSizes[buttonSize]} ${className || ""}`}></i>
}

export default Button
